import { useState, useEffect, useRef, useCallback } from "react";
import io from "socket.io-client";
import { API_BASE_URL } from "../api/config";
import { getChat, sendChat } from "../api/chat.api";
import { getStaffId } from "../utils/storage";
import { REMARKS_MAX } from "../utils/profileValidation";
import { toast } from "../Components/Toast";

export const chatSocket = io(API_BASE_URL);

/**
 * @param {string|null|undefined} doctorId
 */
export function useDoctorChat(doctorId) {
  const [staffId, setStaffId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [file, setFile] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const id = getStaffId();
    if (!id) setError("Staff ID is not available.");
    else setStaffId(id);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!doctorId || !staffId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;

    const fetchHistory = async () => {
      try {
        const response = await getChat(staffId, doctorId);
        if (!cancelled) {
          if (response.data.success) setMessages(response.data.messages || []);
          else setMessages([]);
        }
      } catch {
        if (!cancelled) setMessages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHistory();
    chatSocket.emit("joinRoom", { staffId, doctorId });

    const onReceive = (message) => {
      setMessages((prev) => [...prev, message]);
    };
    const onTyping = (senderId) => {
      if (senderId !== staffId) {
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 2000);
      }
    };

    chatSocket.on("receiveMessage", onReceive);
    chatSocket.on("typing", onTyping);

    return () => {
      cancelled = true;
      chatSocket.off("receiveMessage", onReceive);
      chatSocket.off("typing", onTyping);
    };
  }, [staffId, doctorId]);

  const sendMessage = useCallback(async () => {
    if (!doctorId || !staffId || (!newMessage.trim() && !file)) return;
    if (newMessage.length > REMARKS_MAX) return;

    try {
      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("message", newMessage);
        formData.append("senderType", "staff");
        const response = await sendChat(staffId, doctorId, formData);
        if (response.data.success) {
          chatSocket.emit("sendMessage", response.data.chat);
          setFile(null);
          setNewMessage("");
        }
      } else {
        const response = await sendChat(staffId, doctorId, {
          staffId,
          doctorId,
          message: newMessage,
          senderType: "staff",
        });
        if (response.data.success) {
          chatSocket.emit("sendMessage", response.data.chat);
          setNewMessage("");
        }
      }
    } catch {
      const msg = "Error sending message.";
      setError(msg);
      toast.error(msg);
    }
  }, [doctorId, staffId, newMessage, file]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      } else if (staffId) {
        chatSocket.emit("typing", staffId);
      }
    },
    [sendMessage, staffId]
  );

  const clearChatState = useCallback(() => {
    setMessages([]);
    setNewMessage("");
    setFile(null);
    setError(null);
  }, []);

  return {
    staffId,
    messages,
    setMessages,
    loading,
    newMessage,
    setNewMessage,
    file,
    setFile,
    isTyping,
    error,
    setError,
    messagesEndRef,
    fileInputRef,
    sendMessage,
    handleKeyPress,
    clearChatState,
    remarksMax: REMARKS_MAX,
    chatDisabled: !doctorId || !staffId,
  };
}
